import { useState, useEffect, useCallback, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { MoonLoader } from "react-spinners";
import { Row, Col } from "react-bootstrap";
import {
  IconBrain,
  IconCurrency,
  IconTrendingUp,
  IconTrendingDown,
  IconRefresh,
  IconAlertTriangle,
  IconChartPie,
  IconChartBar,
  IconCurrencyDollar,
  IconMessageCircle,
  IconBuildingStore,
  IconDeviceAnalytics,
  IconGrowth,
  IconMoneybag
} from "@tabler/icons-react";
import DashboardService from "./DashboardServices";
import "./Dashboard.css";

// =============================================== //
// UTILITÁRIOS PARA VALIDAÇÃO DE DADOS            //
// =============================================== //
const validateSeriesData = (series, seriesName) => {
  if (!Array.isArray(series)) {
    console.warn(`⚠️ AgencyDashboard: ${seriesName} series não é um array:`, series);
    return [{ name: "Sem dados", data: [0] }];
  }
  
  if (series.length === 0) {
    console.warn(`⚠️ AgencyDashboard: ${seriesName} series está vazio`);
    return [{ name: "Sem dados", data: [0] }];
  }
  
  const validSeries = series.map((s, index) => {
    if (!s || typeof s !== 'object') {
      console.warn(`⚠️ AgencyDashboard: ${seriesName} série ${index} inválida:`, s);
      return { name: `Série ${index + 1}`, data: [0] };
    }
    
    // Criar uma cópia para não mutar o objeto original
    const validSerie = {
      name: s.name && typeof s.name === 'string' ? s.name : `Série ${index + 1}`,
      data: []
    };
    
    if (!Array.isArray(s.data)) {
      console.warn(`⚠️ AgencyDashboard: ${seriesName} série ${index} data não é array:`, s.data);
      validSerie.data = [0];
    } else if (s.data.length === 0) {
      console.warn(`⚠️ AgencyDashboard: ${seriesName} série ${index} data está vazio`);
      validSerie.data = [0];
    } else {
      // Validar valores numéricos e filtrar valores inválidos
      validSerie.data = s.data.map(val => {
        if (val == null || val === undefined || val === '') {
          return 0;
        }
        const num = Number(val);
        return isNaN(num) ? 0 : Math.max(0, num); // Garante valores não negativos
      });
      
      // Garantir que há pelo menos um valor
      if (validSerie.data.length === 0) {
        validSerie.data = [0];
      }
    }
    
    return validSerie;
  });
  
  // Garantir que sempre há pelo menos uma série válida
  if (validSeries.length === 0) {
    console.warn(`⚠️ AgencyDashboard: Todas as séries de ${seriesName} foram filtradas, usando dados padrão`);
    return [{ name: "Sem dados", data: [0] }];
  }
  
  console.log(`✅ AgencyDashboard: ${seriesName} séries validadas:`, validSeries);
  return validSeries;
};

const validateLabelsData = (labels, labelsName) => {
  if (!Array.isArray(labels)) {
    console.warn(`⚠️ AgencyDashboard: ${labelsName} labels não é um array:`, labels);
    return ["Sem dados"];
  }
  
  if (labels.length === 0) {
    console.warn(`⚠️ AgencyDashboard: ${labelsName} labels está vazio`);
    return ["Sem dados"];
  }
  
  const validLabels = labels.map((label, index) => {
    if (typeof label !== 'string' && typeof label !== 'number') {
      console.warn(`⚠️ AgencyDashboard: ${labelsName} label ${index} inválido:`, label);
      return `Item ${index + 1}`;
    }
    return String(label);
  });
  
  console.log(`✅ AgencyDashboard: ${labelsName} labels validados:`, validLabels);
  return validLabels;
};

// =============================================== //
// COMPONENTES DE SKELETON MELHORADOS             //
// =============================================== //
const MetricCardSkeleton = () => (
  <div className="metric-card skeleton slide-up">
    <div className="metric-header">
      <div className="metric-icon skeleton-shimmer"></div>
      <div className="metric-trend skeleton-shimmer skeleton-trend"></div>
    </div>
    <div className="metric-value skeleton-shimmer skeleton-value"></div>
    <div className="metric-label skeleton-shimmer skeleton-label"></div>
    <div className="metric-description skeleton-shimmer skeleton-description"></div>
  </div>
);

const ChartCardSkeleton = ({ height = "300px" }) => (
  <div className="chart-card skeleton slide-up">
    <div className="chart-header">
      <div className="chart-title skeleton-shimmer skeleton-chart-title"></div>
      <div className="chart-period skeleton-shimmer skeleton-chart-period"></div>
    </div>
    <div className="chart-content skeleton-shimmer" style={{ height }}></div>
  </div>
);

// =============================================== //
// COMPONENTE PRINCIPAL AGENCY DASHBOARD           //
// =============================================== //
const AgencyDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Detector de mobile
  const isMobile = useMemo(() => window.innerWidth <= 768, []);

  // =============================================== //
  // FUNÇÕES DE CARREGAMENTO DE DADOS               //
  // =============================================== //
  const loadDashboardData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError(null);

    try {
      // Aqui você deve implementar a chamada para a API de planos de agência
      const data = await DashboardService.getAgencyMetrics();
      setMetrics(data);
    } catch (err) {
      console.error("Erro ao carregar dashboard da agência:", err);
      
      if (err.response?.status === 403) {
        setError("Este dashboard está disponível apenas para agências.");
      } else {
        setError("Erro ao carregar dados do dashboard. Tente novamente.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleRefresh = useCallback(() => {
    DashboardService.clearCache();
    loadDashboardData(true);
  }, [loadDashboardData]);

  // =============================================== //
  // CONFIGURAÇÕES DOS GRÁFICOS OTIMIZADAS          //
  // =============================================== //
  const chartOptions = useMemo(() => {
    const baseOptions = {
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: {
          enabled: !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
          easing: "easeinout",
          speed: 600,
        },
        fontFamily: "inherit",
      },
      grid: {
        borderColor: "#f1f3f4",
        strokeDashArray: 2,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { left: 10, right: 10 },
      },
      xaxis: {
        labels: {
          style: {
            colors: "#6c757d",
            fontSize: isMobile ? "10px" : "11px",
            fontWeight: 500,
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#6c757d",
            fontSize: isMobile ? "10px" : "11px",
            fontWeight: 500,
          },
        },
      },
      tooltip: {
        theme: "light",
        style: { fontSize: "12px" },
        marker: { show: true },
      },
    };

    return {
      // Configuração para gráfico de linha mensal (agentes)
      agentsTrend: {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: "line",
          height: isMobile ? 280 : 350,
        },
        colors: ["#2bc155", "var(--primary)"],
        stroke: {
          curve: "smooth",
          width: 3,
        },
        markers: {
          size: 4,
          hover: { size: 6 },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
          fontSize: "11px",
          fontWeight: 500,
          markers: { radius: 3 },
        },
        tooltip: {
          ...baseOptions.tooltip,
          y: {
            formatter: (value) => `${value} agentes`,
          },
        },
      },

      // Configuração para gráfico de área (mensagens)
      messages: {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: "area",
          height: isMobile ? 240 : 300,
        },
        colors: ["#ff8c00", "var(--primary)"],
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.3,
            opacityTo: 0.05,
            stops: [0, 100],
          },
        },
        stroke: {
          curve: "smooth",
          width: 2,
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
          fontSize: "11px",
          fontWeight: 500,
          markers: { radius: 3 },
        },
      },

      // Configuração para donut (status de contas e pagamentos)
      donut: {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: "donut",
          height: isMobile ? 220 : 280,
        },
        colors: ["#2bc155", "var(--primary)", "#ffc107", "#dc3545"],
        legend: {
          position: "bottom",
          fontSize: isMobile ? "10px" : "11px",
          fontWeight: 500,
          markers: { radius: 3 },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "60%",
              labels: {
                show: true,
                total: {
                  show: true,
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: 600,
                  color: "#495057",
                },
              },
            },
          },
        },
        tooltip: {
          ...baseOptions.tooltip,
          y: {
            formatter: (value) => `${value} itens`,
          },
        },
      },

      // Configuração para coluna (receita e fees)
      column: {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: "column",
          height: isMobile ? 240 : 300,
        },
        colors: ["#2bc155", "#137ff7", "#ffc107"],
        plotOptions: {
          bar: {
            columnWidth: "50%",
            borderRadius: 4,
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "left",
          fontSize: "11px",
          fontWeight: 500,
          markers: { radius: 3 },
        },
        tooltip: {
          ...baseOptions.tooltip,
          y: {
            formatter: (value) => `R$ ${value.toLocaleString()}`,
          },
        },
      },
    };
  }, [isMobile]);

  // =============================================== //
  // PREPARAÇÃO DOS DADOS DOS GRÁFICOS              //
  // =============================================== //
  const chartData = useMemo(() => {
    console.log("🔍 AgencyDashboard: Preparando dados dos gráficos", { metrics });
    
    if (!metrics) {
      console.log("⚠️ AgencyDashboard: Metrics não disponível, retornando estrutura vazia");
      return {
        agentsTrend: { series: [], categories: [] },
        messages: { series: [], categories: [] },
        subaccounts: { series: [], labels: [] },
        deployments: { series: [], labels: [] },
        payments: { series: [], labels: [] },
        revenue: { series: [], categories: [] }
      };
    }

    // 1. EVOLUÇÃO DE AGENTES (criados vs implementados)
    const agentGrowthData = metrics.agent_metrics?.growth_data || {};
    const rawAgentsSeries = [
      {
        name: "Criados",
        data: [agentGrowthData.created_previous || 0, agentGrowthData.created_current || 0]
      },
      {
        name: "Implementados", 
        data: [agentGrowthData.implemented_previous || 0, agentGrowthData.implemented_current || 0]
      }
    ];
    const agentsSeries = validateSeriesData(rawAgentsSeries, "Agentes");

    // 2. MENSAGENS MENSAIS (últimos dados disponíveis)
    const monthlyVolume = metrics.engagement_metrics?.monthly_volume || [];
    const lastMonth = monthlyVolume.length > 0 ? monthlyVolume[monthlyVolume.length - 1] : {};
    const rawMessagesSeries = [
      { name: "Enviadas", data: [lastMonth.sent || 0] },
      { name: "Recebidas", data: [lastMonth.received || 0] }
    ];
    const messagesSeries = validateSeriesData(rawMessagesSeries, "Mensagens");

    // 3. STATUS DAS SUBCONTAS
    const subaccounts = metrics.organization_overview?.total_subaccounts || {};
    const rawSubaccountsSeries = [subaccounts.active || 0, subaccounts.inactive || 0];
    const subaccountsSeries = rawSubaccountsSeries.map(val => Number(val) || 0);
    const rawSubaccountsLabels = ["Ativas", "Inativas"];
    const subaccountsLabels = validateLabelsData(rawSubaccountsLabels, "Subcontas");

    // 4. STATUS DE IMPLEMENTAÇÕES
    const deployments = metrics.deployment_metrics?.deployments_by_status || {};
    const deploymentsKeys = Object.keys(deployments);
    const hasValidDeploymentsData = deploymentsKeys.length > 0 && Object.values(deployments).some(val => val != null && Number(val) > 0);
    const rawDeploymentsSeries = hasValidDeploymentsData ? Object.values(deployments).map(val => Number(val) || 0).filter(val => val > 0) : [1];
    const deploymentsSeries = rawDeploymentsSeries.length > 0 ? rawDeploymentsSeries : [1];
    const rawDeploymentsLabels = hasValidDeploymentsData ? deploymentsKeys.filter(key => deployments[key] > 0).map(key => {
      const labels = {
        active: "Ativas",
        suspended: "Suspensas", 
        canceled: "Canceladas"
      };
      return labels[key] || key;
    }) : ["Sem dados"];
    const deploymentsLabels = validateLabelsData(rawDeploymentsLabels, "Implementações");

    // 5. STATUS DE PAGAMENTOS
    const hasValidFinancialData = metrics.financial_metrics && 
                                 Object.keys(metrics.financial_metrics).length > 0 && 
                                 (metrics.financial_metrics.payment_status_distribution || metrics.financial_metrics.deployment_status_distribution);
    const payments = hasValidFinancialData ? (metrics.financial_metrics.payment_status_distribution || metrics.financial_metrics.deployment_status_distribution || {}) : {};
    const paymentsKeys = Object.keys(payments);
    const hasValidPaymentsData = paymentsKeys.length > 0 && Object.values(payments).some(val => val != null && Number(val) > 0);
    const rawPaymentsSeries = hasValidPaymentsData ? Object.values(payments).map(val => Number(val) || 0).filter(val => val > 0) : [];
    const paymentsSeries = rawPaymentsSeries.length > 0 ? rawPaymentsSeries : [];
    const rawPaymentsLabels = hasValidPaymentsData ? paymentsKeys.filter(key => payments[key] > 0).map(key => {
      const labels = {
        paid: "Pago",
        pending: "Pendente",
        overdue: "Atrasado",
        canceled: "Cancelado"
      };
      return labels[key] || key;
    }) : [];
    const paymentsLabels = rawPaymentsLabels.length > 0 ? validateLabelsData(rawPaymentsLabels, "Pagamentos") : [];

    // 6. RECEITAS (setup, recorrente, suporte)
    const financial = hasValidFinancialData ? metrics.financial_metrics : {};
    const setupPaid = Number(financial.total_setup_fees?.paid) || 0;
    const monthlyRevenue = Number(financial.monthly_recurring_revenue) || 0;
    const supportFees = Number(financial.total_support_fees) || 0;
    const hasValidRevenueData = hasValidFinancialData && (setupPaid > 0 || monthlyRevenue > 0 || supportFees > 0);
    
    const rawRevenueSeries = hasValidRevenueData ? [
      { name: "Setup Pago", data: [setupPaid] },
      { name: "Receita Recorrente", data: [monthlyRevenue] },
      { name: "Taxas Suporte", data: [supportFees] }
    ].filter(serie => serie.data[0] > 0) : [];
    const revenueSeries = rawRevenueSeries.length > 0 ? validateSeriesData(rawRevenueSeries, "Receitas") : [];

    const result = {
      agentsTrend: {
        series: agentsSeries,
        categories: validateLabelsData(["Mês Anterior", "Mês Atual"], "AgentsTrend Categories")
      },
      messages: {
        series: messagesSeries,
        categories: validateLabelsData([lastMonth.month || "Atual"], "Messages Categories")
      },
      subaccounts: {
        series: subaccountsSeries.length > 0 && subaccountsSeries.some(val => val > 0) ? subaccountsSeries : [1],
        labels: subaccountsSeries.length > 0 && subaccountsSeries.some(val => val > 0) ? subaccountsLabels : ["Sem dados"]
      },
      deployments: {
        series: deploymentsSeries,
        labels: deploymentsLabels
      },
      payments: {
        series: paymentsSeries.length > 0 ? paymentsSeries : [],
        labels: paymentsLabels.length > 0 ? paymentsLabels : []
      },
      revenue: {
        series: revenueSeries.length > 0 ? revenueSeries : [],
        categories: revenueSeries.length > 0 ? validateLabelsData(["Receitas"], "Revenue Categories") : []
      }
    };

    console.log("📊 AgencyDashboard: Dados finalizados dos gráficos:", result);
    return result;
  }, [metrics]);

  // =============================================== //
  // MÉTRICAS PRINCIPAIS (6 CARDS)                  //
  // =============================================== //
  const primaryMetrics = useMemo(() => {
    if (!metrics) return [];

    const orgOverview = metrics.organization_overview || {};
    const agentMetrics = metrics.agent_metrics || {};
    const financialMetrics = metrics.financial_metrics || {};
    const deploymentMetrics = metrics.deployment_metrics || {};
    const engagementMetrics = metrics.engagement_metrics || {};

    return [
      {
        id: "total_subaccounts",
        icon: IconBuildingStore,
        value: orgOverview.total_subaccounts?.total || 0,
        label: "Total Subcontas",
        description: `${orgOverview.total_subaccounts?.active || 0} ativas | ${orgOverview.total_subaccounts?.inactive || 0} inativas`,
        trend: (orgOverview.total_subaccounts?.active || 0) > (orgOverview.total_subaccounts?.inactive || 0) ? "positive" : "neutral",
        growth: `${orgOverview.total_users || 0} usuários`,
        priority: 1,
        color: "linear-gradient(135deg, #137ff7, #1ea7c5)",
      },
      {
        id: "agents_created",
        icon: IconBrain,
        value: agentMetrics.total_agents_created || 0,
        label: "Agentes Criados",
        description: `${agentMetrics.agents_implemented || 0} implementados`,
        trend: (agentMetrics.conversion_rate || 0) >= 70 ? "positive" : "neutral",
        growth: `${agentMetrics.conversion_rate || 0}% conversão`,
        priority: 2,
        color: "linear-gradient(135deg, #2bc155, #20c997)",
      },
      {
        id: "total_revenue",
        icon: IconCurrencyDollar,
        value: `R$ ${(financialMetrics.total_accumulated_revenue || 0).toLocaleString()}`,
        label: "Receita Acumulada",
        description: `R$ ${(financialMetrics.monthly_recurring_revenue || 0).toLocaleString()} recorrente`,
        trend: (financialMetrics.total_accumulated_revenue || 0) > 0 ? "positive" : "neutral",
        growth: `${(financialMetrics.payment_status_distribution?.paid || financialMetrics.deployment_status_distribution?.paid || 0)} pagos`,
        priority: 3,
        color: "linear-gradient(135deg, var(--primary), #de3096)",
      },
      {
        id: "total_deployments",
        icon: IconDeviceAnalytics,
        value: deploymentMetrics.total_deployments || 0,
        label: "Implementações",
        description: `${deploymentMetrics.deployments_by_status?.active || 0} ativas`,
        trend: (deploymentMetrics.churn_rate || 0) < 10 ? "positive" : "negative",
        growth: `${deploymentMetrics.churn_rate || 0}% churn`,
        priority: 4,
        color: "linear-gradient(135deg, #1ea7c5, #137ff7)",
      },
      {
        id: "total_messages",
        icon: IconMessageCircle,
        value: (engagementMetrics.total_messages?.total || 0).toLocaleString(),
        label: "Total Mensagens",
        description: `${(engagementMetrics.total_messages?.sent || 0).toLocaleString()} enviadas | ${(engagementMetrics.total_messages?.received || 0).toLocaleString()} recebidas`,
        trend: (engagementMetrics.total_messages?.total || 0) > 0 ? "positive" : "neutral",
        growth: "Comunicação ativa",
        priority: 5,
        color: "linear-gradient(135deg, #6f42c1, #e83e8c)",
      },
      {
        id: "setup_fees",
        icon: IconMoneybag,
        value: `R$ ${(financialMetrics.total_setup_fees?.paid || 0).toLocaleString()}`,
        label: "Setup Fees Pagos",
        description: `R$ ${(financialMetrics.total_setup_fees?.pending || 0).toLocaleString()} pendentes`,
        trend: (financialMetrics.total_setup_fees?.paid || 0) > (financialMetrics.total_setup_fees?.pending || 0) ? "positive" : "neutral",
        growth: `R$ ${(financialMetrics.total_support_fees || 0).toLocaleString()} suporte`,
        priority: 6,
        color: "linear-gradient(135deg, #ff8c00, #ffc107)",
      },
    ];
  }, [metrics]);

  // =============================================== //
  // RENDERIZAÇÃO DOS SKELETONS                      //
  // =============================================== //
  if (loading && !refreshing) {
    return (
      <div className="">
        <div className="dashboard-grid grid-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>

        <Row className="mt-4">
          <Col xs={12} lg={8} className="mb-3">
            <ChartCardSkeleton />
          </Col>
          <Col xs={12} lg={4} className="mb-3">
            <ChartCardSkeleton height="280px" />
          </Col>
        </Row>

        <Row className="mt-4">
          <Col xs={12} lg={6} className="mb-3">
            <ChartCardSkeleton />
          </Col>
          <Col xs={12} lg={6} className="mb-3">
            <ChartCardSkeleton />
          </Col>
        </Row>

        <Row className="mt-4">
          <Col xs={12} lg={4} className="mb-3">
            <ChartCardSkeleton />
          </Col>
          <Col xs={12} lg={4} className="mb-3">
            <ChartCardSkeleton />
          </Col>
          <Col xs={12} lg={4} className="mb-3">
            <ChartCardSkeleton />
          </Col>
        </Row>
      </div>
    );
  }

  // =============================================== //
  // RENDERIZAÇÃO DE ERRO                            //
  // =============================================== //
  if (error) {
    return (
      <div className="error-container">
        <IconAlertTriangle className="error-icon" />
        <h3 className="error-title">Ops! Algo deu errado</h3>
        <p className="error-message">{error}</p>
        <button className="error-button" onClick={handleRefresh}>
          <IconRefresh size={16} />
          Tentar Novamente
        </button>
      </div>
    );
  }

  // =============================================== //
  // RENDERIZAÇÃO PRINCIPAL                          //
  // =============================================== //
  return (
    <div className="dashboard">
      {/* 1ª LINHA - MÉTRICAS PRINCIPAIS (6 CARDS) */}
      <section className="metrics-section">
        <div className="section-header">
          <h4 className="section-title">
            <IconChartBar size={20} />
            Visão Geral da Agência
          </h4>
          {metrics?.metadata && (
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? (
                <MoonLoader size={12} />
              ) : (
                <IconRefresh className="mr-1" size={16} />
              )}
              {window.innerWidth > 768
                ? refreshing
                  ? "Atualizando..."
                  : "Atualizar"
                : null}
            </button>
          )}
        </div>

        <div className="dashboard-grid grid-3">
          {primaryMetrics.map((metric, index) => {
            const IconComponent = metric.icon;
            return (
              <div
                key={metric.id}
                className="metric-card fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="metric-header">
                  <div
                    className="metric-icon"
                    style={metric.color ? { background: metric.color } : {}}
                  >
                    <IconComponent size={20} />
                  </div>
                  <div className={`metric-trend ${metric.trend}`}>
                    {metric.trend === "positive" ? (
                      <IconTrendingUp size={10} />
                    ) : metric.trend === "negative" ? (
                      <IconTrendingDown size={10} />
                    ) : (
                      <span style={{ fontSize: "8px" }}></span>
                    )}
                    {metric.growth}
                  </div>
                </div>
                <div className="metric-value">{metric.value}</div>
                <h3 className="metric-label">{metric.label}</h3>
                <p className="metric-description">{metric.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* 2ª LINHA - EVOLUÇÃO DE AGENTES E SUBCONTAS */}
      <section className="charts-section">
        <div className="section-header">
          <h4 className="section-title">
            <IconBrain size={20} />
            Crescimento e Desenvolvimento
          </h4>
        </div>

        <Row>
          <Col xs={12} lg={8} className="mb-4">
            <div className="chart-card fade-in">
              <div className="chart-header">
                <h3 className="chart-title">
                  <IconGrowth size={16} />
                  Evolução de Agentes
                </h3>
                <span className="chart-period">
                  Crescimento mensal
                </span>
              </div>
              <div className="chart-content">
                {(() => {
                  const hasValidAgents = chartData?.agentsTrend?.series && 
                                        Array.isArray(chartData.agentsTrend.series) && 
                                        chartData.agentsTrend.series.length > 0;
                  
                  if (!hasValidAgents) {
                    return (
                      <div className="text-center text-muted py-4">
                        <p>Nenhum dado disponível</p>
                      </div>
                    );
                  }

                  try {
                    return (
                      <ReactApexChart
                        options={{
                          ...chartOptions.agentsTrend,
                          xaxis: {
                            ...(chartOptions.agentsTrend?.xaxis || {}),
                            categories: chartData.agentsTrend.categories || ["Sem dados"],
                          },
                        }}
                        series={chartData.agentsTrend.series}
                        type="line"
                        height={350}
                      />
                    );
                  } catch (error) {
                    console.error('Erro ao renderizar gráfico de agentes:', error);
                    return (
                      <div className="text-center text-muted py-4">
                        <p>Nenhum dado disponível</p>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          </Col>

          <Col xs={12} lg={4} className="mb-4">
            <div className="chart-card fade-in" style={{ animationDelay: "0.1s" }}>
              <div className="chart-header">
                <h3 className="chart-title">
                  <IconBuildingStore size={16} />
                  Status das Subcontas
                </h3>
                <span className="chart-period">Total atual</span>
              </div>
              <div className="chart-content">
                {(() => {
                  const hasValidSubaccounts = chartData?.subaccounts?.series && 
                                             Array.isArray(chartData.subaccounts.series) && 
                                             chartData.subaccounts.series.length > 0;
                  
                  if (!hasValidSubaccounts) {
                    return (
                      <div className="text-center text-muted py-4">
                        <p>Nenhum dado disponível</p>
                      </div>
                    );
                  }

                  try {
                    return (
                      <ReactApexChart
                        options={{
                          ...chartOptions.donut,
                          labels: chartData.subaccounts.labels || ["Sem dados"],
                        }}
                        series={chartData.subaccounts.series}
                        type="donut"
                        height={280}
                      />
                    );
                  } catch (error) {
                    console.error('Erro ao renderizar gráfico de subcontas:', error);
                    return (
                      <div className="text-center text-muted py-4">
                        <p>Nenhum dado disponível</p>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* 3ª LINHA - COMUNICAÇÃO E IMPLEMENTAÇÕES */}
      <section className="charts-section">
        <div className="section-header">
          <h4 className="section-title">
            <IconMessageCircle size={20} />
            Comunicação e Implementações
          </h4>
        </div>

        <Row>
          <Col xs={12} lg={6} className="mb-4">
            <div className="chart-card fade-in">
              <div className="chart-header">
                <h3 className="chart-title">
                  <IconMessageCircle size={16} />
                  Volume de Mensagens
                </h3>
                <span className="chart-period">
                  Último mês
                </span>
              </div>
              <div className="chart-content">
                {(() => {
                  const hasValidMessages = chartData?.messages?.series && 
                                          Array.isArray(chartData.messages.series) && 
                                          chartData.messages.series.length > 0;
                  
                  if (!hasValidMessages) {
                    return (
                      <div className="text-center text-muted py-4">
                        <p>Nenhum dado disponível</p>
                      </div>
                    );
                  }

                  try {
                    return (
                      <ReactApexChart
                        options={{
                          ...chartOptions.messages,
                          xaxis: {
                            ...(chartOptions.messages?.xaxis || {}),
                            categories: chartData.messages.categories || ["Sem dados"],
                          },
                        }}
                        series={chartData.messages.series}
                        type="area"
                        height={300}
                      />
                    );
                  } catch (error) {
                    console.error('Erro ao renderizar gráfico de mensagens:', error);
                    return (
                      <div className="text-center text-muted py-4">
                        <p>Nenhum dado disponível</p>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          </Col>

          <Col xs={12} lg={6} className="mb-4">
            <div className="chart-card fade-in" style={{ animationDelay: "0.1s" }}>
              <div className="chart-header">
                <h3 className="chart-title">
                  <IconDeviceAnalytics size={16} />
                  Status Implementações
                </h3>
                <span className="chart-period">Situação atual</span>
              </div>
              <div className="chart-content">
                {(() => {
                  const hasValidDeployments = chartData?.deployments?.series && 
                                             Array.isArray(chartData.deployments.series) && 
                                             chartData.deployments.series.length > 0;
                  
                  if (!hasValidDeployments) {
                    return (
                      <div className="text-center text-muted py-4">
                        <p>Nenhum dado disponível</p>
                      </div>
                    );
                  }

                  try {
                    return (
                      <ReactApexChart
                        options={{
                          ...chartOptions.donut,
                          labels: chartData.deployments.labels || ["Sem dados"],
                        }}
                        series={chartData.deployments.series}
                        type="donut"
                        height={280}
                      />
                    );
                  } catch (error) {
                    console.error('Erro ao renderizar gráfico de implementações:', error);
                    return (
                      <div className="text-center text-muted py-4">
                        <p>Nenhum dado disponível</p>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* 4ª LINHA - FINANCEIRO */}
      <section className="charts-section">
        <div className="section-header">
          <h4 className="section-title">
            <IconCurrencyDollar size={20} />
            Gestão Financeira
          </h4>
        </div>

        <Row>
          <Col xs={12} lg={8} className="mb-4">
            <div className="chart-card fade-in">
              <div className="chart-header">
                <h3 className="chart-title">
                  <IconMoneybag size={16} />
                  Receitas por Tipo
                </h3>
                <span className="chart-period">Valores atuais</span>
              </div>
              <div className="chart-content">
                {(() => {
                  const hasValidRevenue = chartData?.revenue?.series && 
                                         Array.isArray(chartData.revenue.series) && 
                                         chartData.revenue.series.length > 0 &&
                                         chartData.revenue.categories &&
                                         Array.isArray(chartData.revenue.categories) &&
                                         chartData.revenue.categories.length > 0;
                  
                  if (!hasValidRevenue) {
                    return (
                      <div className="text-center text-muted py-4">
                        <p>Nenhum dado disponível</p>
                      </div>
                    );
                  }

                  try {
                    return (
                      <ReactApexChart
                        options={{
                          ...chartOptions.column,
                          xaxis: {
                            ...(chartOptions.column?.xaxis || {}),
                            categories: chartData.revenue.categories,
                          },
                        }}
                        series={chartData.revenue.series}
                        type="column"
                        height={300}
                      />
                    );
                  } catch (error) {
                    console.error('Erro ao renderizar gráfico de receitas:', error);
                    return (
                      <div className="text-center text-muted py-4">
                        <p>Nenhum dado disponível</p>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          </Col>

          <Col xs={12} lg={4} className="mb-4">
            <div className="chart-card fade-in" style={{ animationDelay: "0.1s" }}>
              <div className="chart-header">
                <h3 className="chart-title">
                  <IconCurrency size={16} />
                  Status de Pagamentos
                </h3>
                <span className="chart-period">Distribuição atual</span>
              </div>
              <div className="chart-content">
                {(() => {
                  const hasValidPayments = chartData?.payments?.series && 
                                          Array.isArray(chartData.payments.series) && 
                                          chartData.payments.series.length > 0 &&
                                          chartData.payments.series.every(val => typeof val === 'number' && !isNaN(val) && val > 0) &&
                                          chartData.payments.labels &&
                                          Array.isArray(chartData.payments.labels) &&
                                          chartData.payments.labels.length > 0;
                  
                  if (!hasValidPayments) {
                    return (
                      <div className="text-center text-muted py-4">
                        <p>Nenhum dado disponível</p>
                      </div>
                    );
                  }

                  try {
                    return (
                      <ReactApexChart
                        options={{
                          ...chartOptions.donut,
                          labels: chartData.payments.labels,
                        }}
                        series={chartData.payments.series}
                        type="donut"
                        height={280}
                      />
                    );
                  } catch (error) {
                    console.error('Erro ao renderizar gráfico de pagamentos:', error);
                    return (
                      <div className="text-center text-muted py-4">
                        <p>Nenhum dado disponível</p>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          </Col>
        </Row>
      </section>

    
    </div>
  );
};

export default AgencyDashboard;